import React from "react";
import { Phone, Star, ShieldCheck, ArrowRight } from "lucide-react";
import { SITE, BRAND_POSTERS } from "../site.config";
import WhatsAppButton from "./WhatsAppButton";

const TRUST_BADGES = [
  "Free Measure & Quote",
  "Fully Insured",
  "20+ Years",
  "Domestic & Commercial",
];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative bg-moffat-cream overflow-hidden"
    >
      {/* Top decorative red bar */}
      <div className="h-1.5 bg-moffat-red" />

      {/* ===== New branded banner (full bleed) ===== */}
      <div
        data-testid="hero-banner"
        className="relative w-full overflow-hidden animate-float-up bg-moffat-charcoal"
      >
        <img
          src={SITE.heroBannerUrl}
          alt="Quality flooring expertly fitted — Carpet & Lino fitted by A.Moffat"
          className="block w-full h-auto"
          fetchPriority="high"
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-6 md:pt-8 md:pb-8">
        {/* CTA strip directly below banner */}
        <div className="flex flex-col md:flex-row md:flex-wrap md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap items-stretch gap-3">
            <a
              data-testid="hero-quote-cta"
              href="#quote"
              className="group inline-flex items-center justify-center gap-2 bg-moffat-red hover:bg-moffat-redDeep text-white font-bold text-sm md:text-base px-6 h-14 rounded-sm shadow-lg shadow-moffat-red/25 transition-all hover:-translate-y-0.5 whitespace-nowrap"
            >
              GET MY FREE QUOTE
              <ArrowRight
                size={18}
                strokeWidth={2.5}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
            <WhatsAppButton variant="hero" testId="hero-whatsapp-cta" />
            <a
              data-testid="hero-call-link"
              href={`tel:${SITE.phoneTel}`}
              className="inline-flex items-center justify-center gap-2 bg-moffat-navy hover:bg-moffat-navyDeep text-white font-bold text-sm md:text-base px-6 h-14 rounded-sm shadow-lg transition-all hover:-translate-y-0.5 whitespace-nowrap"
            >
              <Phone size={16} strokeWidth={2.8} />
              CALL US TODAY
            </a>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-0.5 text-yellow-500">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={18} fill="currentColor" strokeWidth={0} />
              ))}
            </div>
            <span className="text-sm font-semibold text-moffat-text">
              5★ on Google &amp; Facebook
            </span>
          </div>
        </div>
      </div>

      {/* ===== Bold typography hero (previous design) ===== */}
      <div className="relative pt-4 pb-12 md:pt-8 md:pb-20">
        {/* Decorative diagonal accents */}
        <div
          aria-hidden="true"
          className="absolute top-0 right-0 w-40 md:w-60 h-full bg-moffat-red rotate-[10deg] origin-top-right translate-x-1/4 opacity-95"
        />
        <div
          aria-hidden="true"
          className="absolute top-0 right-20 md:right-40 w-6 md:w-10 h-full bg-moffat-navy rotate-[10deg] origin-top-right translate-x-1/4"
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            {/* Left text */}
            <div className="lg:col-span-7 animate-float-up">
              <div className="inline-flex items-center gap-2 bg-moffat-navy text-white rounded-full px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] mb-6">
                <span className="h-2 w-2 rounded-full bg-moffat-red" />
                Carpet & Lino Fitted by A.Moffat
              </div>

              <h1
                data-testid="hero-headline"
                className="font-display text-[40px] xs:text-5xl sm:text-6xl lg:text-[88px] text-moffat-charcoal uppercase"
              >
                Quality
                <br />
                Flooring{" "}
                <span className="font-script text-moffat-red lowercase normal-case tracking-normal text-[58px] sm:text-7xl lg:text-[110px] leading-none align-middle">
                  fitted to
                </span>{" "}
                <span className="relative inline-block">
                  <span className="brush-underline text-moffat-navy">
                    PERFECTION.
                  </span>
                </span>
              </h1>

              <p className="mt-7 text-base md:text-lg text-moffat-muted max-w-xl leading-relaxed">
                Carpets, Lino, Vinyl, LVT, stair runners and full subfloor
                preparation — supplied and expertly fitted by a trusted local
                specialist with over{" "}
                <strong className="text-moffat-navy">20 years' experience</strong>.
                Proudly covering County Durham & the North East.
              </p>

              {/* Trust pills */}
              <div className="mt-6 flex flex-wrap gap-2">
                {TRUST_BADGES.map((b) => (
                  <span
                    key={b}
                    className="inline-flex items-center gap-1.5 bg-white border border-moffat-line rounded-full px-3 py-1.5 text-xs font-bold text-moffat-navy"
                  >
                    <ShieldCheck
                      size={12}
                      strokeWidth={3}
                      className="text-moffat-red"
                    />
                    {b}
                  </span>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  data-testid="hero-quote-cta-secondary"
                  href="#quote"
                  className="group inline-flex items-center justify-center gap-2 bg-moffat-red hover:bg-moffat-redDeep text-white font-bold text-sm md:text-base px-6 h-14 rounded-sm shadow-lg shadow-moffat-red/25 transition-all hover:-translate-y-0.5 whitespace-nowrap"
                >
                  GET MY FREE QUOTE
                  <ArrowRight
                    size={18}
                    strokeWidth={2.5}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </a>
                <WhatsAppButton
                  variant="hero"
                  testId="hero-whatsapp-cta-secondary"
                />
                <a
                  data-testid="hero-call-link-secondary"
                  href={`tel:${SITE.phoneTel}`}
                  className="inline-flex items-center justify-center gap-2 bg-moffat-navy hover:bg-moffat-navyDeep text-white font-bold text-sm md:text-base px-6 h-14 rounded-sm shadow-lg transition-all hover:-translate-y-0.5 whitespace-nowrap"
                >
                  <Phone size={16} strokeWidth={2.8} />
                  CALL US TODAY
                </a>
              </div>
            </div>

            {/* Right visual stack */}
            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[4/5] w-full max-w-md mx-auto lg:max-w-none">
                <div className="absolute inset-0 rounded-md overflow-hidden border-4 border-white shadow-2xl bg-moffat-navy">
                  <img
                    src="https://images.unsplash.com/photo-1769736436809-eab3de70b175?auto=format&fit=crop&w=1400&q=80"
                    alt="Beautiful fitted herringbone LVT flooring"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-moffat-navyDeep/70 via-transparent to-transparent" />
                </div>

                {/* Logo badge */}
                <div className="absolute -top-5 -left-5 md:-left-8 h-24 w-24 md:h-32 md:w-32 rounded-full bg-white shadow-xl p-1.5 border-4 border-moffat-cream z-10">
                  <img
                    src={SITE.logoUrl}
                    alt="Logo"
                    className="h-full w-full object-contain rounded-full"
                  />
                </div>

                {/* Floating stat card */}
                <div className="absolute -bottom-6 -right-3 md:-right-6 bg-white rounded-sm shadow-2xl p-4 md:p-5 border-l-4 border-moffat-red z-10">
                  <p className="font-display text-3xl md:text-4xl text-moffat-navy">
                    20<span className="text-moffat-red">+</span>
                  </p>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-moffat-muted mt-1">
                    Years of expert
                    <br />
                    flooring fitting
                  </p>
                </div>

                {/* Tagline ribbon */}
                <div className="absolute bottom-5 left-5 right-5 md:left-6">
                  <p className="font-script text-3xl md:text-4xl text-white drop-shadow-lg">
                    Built to last.
                  </p>
                </div>

                {/* Small thumbnail of brand poster */}
                <div className="hidden md:block absolute -top-4 -right-4 w-32 h-32 lg:w-36 lg:h-36 rounded-sm overflow-hidden border-4 border-white shadow-xl bg-moffat-navy z-10">
                  <img
                    src={BRAND_POSTERS.quote}
                    alt="Free measure & quotation"
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom marquee */}
      <div className="relative bg-moffat-navy text-white py-3 overflow-hidden border-t-4 border-moffat-red">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center shrink-0">
              {[
                "FREE MEASURE & QUOTE",
                "QUALITY WORKMANSHIP",
                "FULLY INSURED",
                "OVER 20 YEARS EXPERIENCE",
                "DOMESTIC & COMMERCIAL",
                "LANDLORD SPECIALISTS",
                "COMPETITIVE PRICES",
                "COVERING COUNTY DURHAM",
              ].map((t) => (
                <span
                  key={t}
                  className="font-display text-sm md:text-base px-6 inline-flex items-center gap-6"
                >
                  {t}
                  <span className="h-1.5 w-1.5 rounded-full bg-moffat-red" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
